/**
 * Совместимость со старым API web-ifc-three.ifcManager для постепенной миграции.
 * Новый runtime — Fragments (@thatopen/components + @thatopen/fragments).
 */
import {
  RenderedFaces,
  SnappingClass,
  type ItemData,
  type MaterialDefinition,
} from "@thatopen/fragments";
import * as THREE from "three";
import { ghostMaterial, highlightMaterial } from "../viewer/scene";
import { getActiveFragmentsModel, getFragmentsManager } from "./engine";

const HIGHLIGHT_STYLE: MaterialDefinition = {
  color: new THREE.Color(0xffbf47),
  renderedFaces: RenderedFaces.TWO,
  opacity: 0.95,
  transparent: true,
};

const GHOST_STYLE: MaterialDefinition = {
  color: new THREE.Color(0x7a8fa3),
  renderedFaces: RenderedFaces.TWO,
  opacity: 0.15,
  transparent: true,
};

function resolveModel(modelId: string | number) {
  const id = String(modelId);
  const model = getActiveFragmentsModel(id);
  if (!model) {
    throw new Error(`Fragments model not found: ${id}`);
  }
  return model;
}

function itemDataToProps(data: ItemData | undefined): Record<string, unknown> {
  if (!data) {
    return {};
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value && typeof value === "object" && "value" in (value as object)) {
      out[key] = (value as { value: unknown }).value;
    } else {
      out[key] = value;
    }
  }
  if (!out.type && out._category) {
    // out._category уже распакован выше из { value } → строка "IFCSLAB" и т.п.
    out.type = out._category;
  }
  return out;
}

function unwrapCategory(value: unknown): string {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object" && "value" in (value as object)) {
    const inner = (value as { value: unknown }).value;
    return typeof inner === "string" ? inner : "";
  }
  return "";
}

export const fragmentsIfcManager = {
  subsets: { items: {} as Record<string, unknown> },

  async getItemProperties(modelId: number | string, expressId: number, _recursive = false) {
    const model = resolveModel(modelId);
    const rows = await model.getItemsData([expressId], {
      attributesDefault: true,
      relations: {
        IsDefinedBy: { attributes: true, relations: false },
        HasAssociations: { attributes: true, relations: false },
      },
    });
    return itemDataToProps(rows[0]);
  },

  async getPropertySets(modelId: number | string, expressId: number, _recursive = false) {
    const model = resolveModel(modelId);
    const rows = await model.getItemsData([expressId], {
      attributesDefault: false,
      relations: {
        IsDefinedBy: { attributes: true, relations: true },
      },
    });
    const rel = rows[0]?.IsDefinedBy;
    if (!Array.isArray(rel)) {
      return [];
    }
    return rel;
  },

  getIfcType(modelId: number | string, expressId: number): string {
    try {
      const model = resolveModel(modelId);
      const item = model.getItem(expressId) as unknown as
        | { category?: unknown; _category?: unknown }
        | undefined;
      return unwrapCategory(item?.category) || unwrapCategory(item?._category);
    } catch {
      return "";
    }
  },

  async getSpatialStructure(modelId: number | string, _includeProperties = false) {
    const model = resolveModel(modelId);
    return model.getSpatialStructure();
  },

  async getMaterialsProperties(_modelId: number | string, _productId: number) {
    return [];
  },

  getExpressId(_geometry: THREE.BufferGeometry, _faceIndex?: number): number | null {
    return null;
  },

  async createSubset(args: {
    modelID: number | string;
    ids: number[];
    material: THREE.Material;
    scene: THREE.Scene;
    removePrevious?: boolean;
    customID?: string;
  }) {
    const model = resolveModel(args.modelID);
    const isGhost = args.material === ghostMaterial || args.customID === "ifc-hidden-ghost";
    let style: MaterialDefinition = isGhost ? GHOST_STYLE : HIGHLIGHT_STYLE;
    const mat = args.material as THREE.MeshLambertMaterial;
    if (!isGhost && mat?.color) {
      style = {
        color: mat.color.clone(),
        renderedFaces: RenderedFaces.TWO,
        opacity: mat.opacity ?? 0.95,
        transparent: mat.transparent ?? true,
      };
    }
    if (args.removePrevious) {
      await model.resetHighlight(args.ids);
    }
    await model.highlight(args.ids, style);
    return model.object;
  },

  async removeSubset(modelId: number | string, _material: THREE.Material, customID?: string) {
    const model = resolveModel(modelId);
    if (customID === "ifc-selected" || customID === "ifc-hidden-ghost") {
      await model.resetHighlight();
    } else if (customID) {
      await model.resetHighlight();
    }
  },

  async close(_modelId: number | string) {
    /* FragmentsModel.dispose вызывается через engine.disposeFragmentsModel */
  },
};

export async function fragmentsRaycast(
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
  mouse: THREE.Vector2,
  dom: HTMLCanvasElement,
  snappingClasses?: SnappingClass[],
) {
  const mgr = getFragmentsManager();
  return mgr.raycast({ camera, mouse, dom, snappingClasses });
}
